
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

export interface Payment {
  id: string;
  plan: string;
  amount: number;
  status: 'success' | 'failed' | 'pending';
  description: string;
  created_at: string;
}

export interface Invoice {
  id: string;
  pdf_url: string;
  amount: number;
  currency: string;
  created_at: string;
}

export function usePaymentHistory() {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();

  const fetchPaymentHistory = async () => {
    if (!user) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // For now, since the tables don't exist yet, we'll return mock data
      // This will be replaced with real data once the Supabase tables are created
      
      // Mock payment data
      const mockPayments: Payment[] = [
        {
          id: '1',
          plan: 'studio_business',
          amount: 59.99,
          status: 'success',
          description: 'Monthly subscription to Studio Business plan',
          created_at: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString() // 30 days ago
        },
        {
          id: '2',
          plan: 'studio_business',
          amount: 59.99,
          status: 'success',
          description: 'Monthly subscription to Studio Business plan',
          created_at: new Date().toISOString()
        }
      ];
      
      // Mock invoice data
      const mockInvoices: Invoice[] = [
        {
          id: '1',
          pdf_url: 'https://example.com/invoice1.pdf',
          amount: 59.99,
          currency: 'EUR',
          created_at: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString() // 30 days ago
        },
        {
          id: '2',
          pdf_url: 'https://example.com/invoice2.pdf',
          amount: 59.99,
          currency: 'EUR',
          created_at: new Date().toISOString()
        }
      ];
      
      setPayments(mockPayments);
      setInvoices(mockInvoices);
      
      // When tables are created, we'll use the following code:
      /*
      // Fetch payment logs
      const { data: paymentData, error: paymentError } = await supabase
        .from('payment_log')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (paymentError) throw paymentError;
      
      // Fetch invoices
      const { data: invoiceData, error: invoiceError } = await supabase
        .from('invoices')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (invoiceError) throw invoiceError;
      
      setPayments(paymentData || []);
      setInvoices(invoiceData || []);
      */
    } catch (err) {
      console.error('Error fetching payment history:', err);
      setError(err instanceof Error ? err : new Error('Unknown error occurred'));
      toast({
        title: "Error loading payment history",
        description: "There was a problem loading your payment history",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    if (user) {
      fetchPaymentHistory();
    }
  }, [user]);
  
  return { payments, invoices, isLoading, error, fetchPaymentHistory };
}
